import React from "react";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import {
    useFormikContext,
} from 'formik';
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import usaStates from "../../../../app/data/common/usaStates";
import { AddEditAddress } from "app/data/address-manager/models";
import { countryOptions, timezoneOptions } from "app/data/terminal-details/models";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import AddressmanagerService from "app/data/address-manager/addressService";
import CommonService from "app/data/common/commonService";

const AddressDetails = ({ isOpen, isEdit }: { isOpen: boolean; isEdit: boolean }): React.ReactElement => {
    const { values, setFieldValue, touched, setFieldTouched } = useFormikContext<AddEditAddress>();

    const addressManagerService = AddressmanagerService.getInstance();

    const isZipValid = values.zip?.length === 5;
    const shouldFetch = (isOpen && isZipValid) && (isEdit ? !!touched?.zip : true);

    const { isFetching } = useQuery({
        queryKey: [QUERY_KEYS.TIMEZONE, values.zip],
        queryFn: async () => {
            if (!values.zip) return;
            const response = await addressManagerService.getTimezone(values.zip);
            return response;
        },
        enabled: shouldFetch,
        onSuccess: (data) => {
            if (!data) {
                return;
            }
            setFieldValue("timezone", data?.timezone);
        },
        refetchOnWindowFocus: false,
        retry: false,
    });

    const { isFetching: isZipcodeLocationsFetching } = useQuery({
        queryKey: [QUERY_KEYS.ZIPCODE_LOCATIONS, values.zip],
        queryFn: async () => {
            if (!values.zip) return;
            const response = await CommonService.getInstance().searchLocations(values.zip);
            return response.data;
        },
        enabled: shouldFetch,
        onSuccess: (data) => {
            if (data?.length === 0) {
                return;
            }
            setFieldValue("city", data?.[0]?.city?.toLowerCase().capitalize());
            setFieldValue("state", data?.[0]?.state);
        },
        refetchOnWindowFocus: false,
        retry: false,
    });


    return (
        <>
            <XGSFormInput
                type="text"
                name="line1"
                required={true}
                labelMode={LabelModes.column}
                label="Street Address Line 1"
            />
            <XGSFormInput
                type="text"
                name="line2"
                required={false}
                labelMode={LabelModes.column}
                label="Street Address Line 2(Optional)"
            />
            <div className="add-edit-address-section__row-item">
                <XGSFormInput
                    type="text"
                    name="city"
                    required={true}
                    labelMode={LabelModes.column}
                    label="City"
                    className="add-edit-address-section__width-50"
                    isLoading={isZipcodeLocationsFetching}
                />
                <XGSFormSelect
                    label="State/Province"
                    name="state"
                    required={true}
                    options={usaStates}
                    value={
                        values.state
                            ? usaStates.find((option) => option.value === values.state)
                            : null
                    }
                    onValueChange={(option) => {
                        setFieldValue("state", option?.value ?? "");
                    }}
                    formik
                    className="add-edit-address-section__width-50"
                    labelMode={LabelModes.column}
                    isLoading={isZipcodeLocationsFetching}
                />
            </div>
            <div className="add-edit-address-section__row-item">
                <XGSFormSelect
                    label="Country"
                    name="country"
                    options={countryOptions}
                    value={
                        values.country
                            ? countryOptions.find((option) => option.value === values.country)
                            : null
                    }
                    onValueChange={(option) => {
                        setFieldValue("country", option?.value ?? "");
                    }}
                    formik
                    className="add-edit-address-section__width-50"
                    labelMode={LabelModes.column}
                />
                <XGSFormInput
                    type="text"
                    name="zip"
                    required={true}
                    labelMode={LabelModes.column}
                    onFocus={() => {
                        setFieldTouched("zip", true);
                    }}
                    label="ZIP/Postal Code"
                    className="add-edit-address-section__width-50"
                />
            </div>
            <div className="add-edit-address-section__row-item">
                <XGSFormSelect
                    label="Timezone"
                    name="timezone"
                    required={true}
                    options={timezoneOptions}
                    value={
                        values.timezone
                            ? timezoneOptions.find(
                                (option) => option.value === values.timezone
                            )
                            : null
                    }
                    onValueChange={(option) => {
                        setFieldValue("timezone", option?.value ?? "");
                    }}
                    formik
                    className="add-edit-address-section__width-50"
                    labelMode={LabelModes.column}
                    isLoading={isFetching}
                />
            </div>
        </>
    );
};


export default AddressDetails;